import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClipboardListIcon, Building2Icon, UsersIcon, CalendarDaysIcon, ArrowRightIcon, ActivityIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useWorkspace } from '../store/workspaceStore';
import { STAGE_META } from '../types';
import { Topbar } from '../components/layout/Topbar';
import { Avatar } from '../components/common/Avatar';
export function Overview() {
  const navigate = useNavigate();
  const proposals = useWorkspace(s => s.proposals);
  const properties = useWorkspace(s => s.properties);
  const leads = useWorkspace(s => s.leads);
  const social = useWorkspace(s => s.social);
  const activity = useWorkspace(s => s.activity);
  const personById = useWorkspace(s => s.personById);
  const me = useWorkspace(s => s.personById(s.currentUserId));
  const openRequests = proposals.filter(p => p.stage !== 'live').length;
  const activeProps = properties.filter(p => p.status === 'active').length;
  const openLeads = leads.filter(l => !['won', 'lost'].includes(l.status)).length;
  const plannedPosts = social.filter(p => p.status !== 'posted').length;
  const stats = [{
    label: 'Open Requests',
    value: openRequests,
    icon: ClipboardListIcon,
    to: '/proposals',
    color: 'text-brand-accent bg-blue-50'
  }, {
    label: 'Active Listings',
    value: activeProps,
    icon: Building2Icon,
    to: '/properties',
    color: 'text-emerald-600 bg-emerald-50'
  }, {
    label: 'Open Leads',
    value: openLeads,
    icon: UsersIcon,
    to: '/leads',
    color: 'text-violet-600 bg-violet-50'
  }, {
    label: 'Posts Scheduled',
    value: plannedPosts,
    icon: CalendarDaysIcon,
    to: '/social',
    color: 'text-orange-600 bg-orange-50'
  }];
  return <div className="flex min-h-full flex-1 flex-col" data-id="element-233" data-mp="1xwbr83w0nced">
      <Topbar title={`Welcome back, ${me?.name.split(' ')[0]}`} subtitle="Everything across the pipeline, in one workspace." data-id="element-234" data-mp="1xtphbxlnkksz" />

      <div className="mp-scroll flex-1 overflow-y-auto p-6" data-id="element-235" data-mp="1ue4gap9lrlq8">
        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-236" data-mp="1zcz98k1aaqo2">
          {stats.map((s, i) => <motion.button key={s.label} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.05
        }} onClick={() => navigate(s.to)} className="rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-card hover:border-brand-accent" data-id="element-237" data-mp="1ld6nl34m70my" data-mpi={i}>
              <div className={`inline-flex rounded-lg p-2 ${s.color}`} data-id="element-238" data-mp="1gfempjlzs46m">
                <s.icon className="h-5 w-5" data-id="element-239" data-mp="12nzzf0l1xgtr" />
              </div>
              <p className="mt-3 text-3xl font-bold text-slate-900" data-id="element-240" data-mp="16o0mtaq4p2cy">{s.value}</p>
              <p className="text-sm text-slate-500" data-id="element-241" data-mp="soiw2pt9vf0y">{s.label}</p>
            </motion.button>)}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_360px]" data-id="element-242" data-mp="xnc8eewtozzj">
          {/* Recent requests */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-card" data-id="element-243" data-mp="e10pflw5gabw">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4" data-id="element-244" data-mp="938xirvi8o7p">
              <h3 className="text-sm font-bold text-slate-800" data-id="element-245" data-mp="o0j69tgrjfzq">Recent Requests</h3>
              <button onClick={() => navigate('/proposals')} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-accent hover:underline" data-id="element-246" data-mp="j0wttm8re3rz">
                View all <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-247" data-mp="wyv2qc4b9803" />
              </button>
            </div>
            <div data-id="element-248" data-mp="s1bhutd3xwz6">
              {proposals.slice(0, 5).map(p => {
              const broker = personById(p.requestedById);
              const done = p.checklist.filter(c => c.status === 'done').length;
              const stage = STAGE_META[p.stage];
              return <button key={p.id} onClick={() => navigate(`/proposals/${p.id}`)} className="flex w-full items-center gap-3 border-b border-slate-50 px-5 py-3.5 text-left last:border-0 hover:bg-slate-50" data-id="element-249" data-mp="1frfjiv6qjmnj">
                    <Avatar person={broker} size={32} data-id="element-250" data-mp="3a633e5m70qth" />
                    <div className="min-w-0 flex-1" data-id="element-251" data-mp="3eycu3nv4tuwt">
                      <p className="truncate text-sm font-semibold text-slate-800" data-id="element-252" data-mp="3eywah054wb8q">{p.property}</p>
                      <p className="truncate text-xs text-slate-400" data-id="element-253" data-mp="3jweag116bg4l">{broker?.name} · {p.deckType}</p>
                    </div>
                    <span className="text-xs font-medium text-slate-400" data-id="element-254" data-mp="2mbrxo8zsusme">{done}/{p.checklist.length}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${stage.color}`} data-id="element-255" data-mp="2w7mp9tm03y1b">{stage.label}</span>
                  </button>;
            })}
            </div>
          </div>

          {/* Activity */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card" data-id="element-256" data-mp="bgcizzua59ez">
            <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-800" data-id="element-257" data-mp="fhy05xduk9uz">
              <ActivityIcon className="h-4 w-4 text-brand-accent" data-id="element-258" data-mp="2019wbzzx4wh1" /> Team Activity
            </h3>
            <div className="mp-scroll max-h-[360px] space-y-4 overflow-y-auto pr-1" data-id="element-259" data-mp="1vz6ij4ps1fsl">
              {activity.slice(0, 12).map(a => {
              const author = personById(a.authorId);
              const prop = proposals.find(p => p.id === a.proposalId);
              return <div key={a.id} className="flex gap-2.5" data-id="element-260" data-mp="1hasgux43jxdm">
                    <Avatar person={author} size={28} data-id="element-261" data-mp="1h85ye2fo3xgm" />
                    <div className="min-w-0" data-id="element-262" data-mp="1caeadji58vhp">
                      <p className="text-sm text-slate-700" data-id="element-263" data-mp="1cds98hrlmmke">
                        <span className="font-semibold" data-id="element-264" data-mp="18adtls7ko87h">{author?.name}</span> {a.text}
                      </p>
                      <p className="text-[11px] text-slate-400" data-id="element-265" data-mp="13clr649ax3p1">
                        {prop ? `${prop.property} · ` : ''}{formatDistanceToNow(new Date(a.createdAt), {
                      addSuffix: true
                    })}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </div>;
}